import type { OrganizationRole, PortalUserListItem } from '@/api/generated';

/**
 * What one row of an organization's portal users list is.
 *
 * Two sections read these rows: staff, on an organization's account page, and
 * the organization's own Super Admin, on the Users page. A section may not
 * import from another, and the same rows read two different ways would show
 * the same person as Invited on one screen and Expired on the other, so the
 * mapping lives here. What may be done to a row is each section's own
 * decision, and stays with it.
 *
 * The backend sends two statuses per row - the membership's, and the
 * invitation's if there is one - and neither alone is the answer.
 */
export type PortalUserState = 'invited' | 'active' | 'expired' | 'revoked' | 'removed';

/** The words shown for each state. Not the stored values, which SHOUT. */
export const STATE_LABELS: Record<PortalUserState, string> = {
  invited: 'Invited',
  active: 'Active',
  expired: 'Expired',
  revoked: 'Revoked',
  removed: 'Removed',
};

/** The words for each role, as a person reads them. */
export const ROLE_LABELS: Record<OrganizationRole, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
};

export interface PortalUserRow {
  /**
   * The row itself. One person can have several - somebody removed and invited
   * again is the old membership and the new one - so this, not the person,
   * tells rows apart.
   */
  membershipId: string;
  /** The person, which is what the role and remove routes take. */
  userId: string;
  invitationId: string | null;
  name: string;
  email: string;
  role: OrganizationRole;
  state: PortalUserState;
  expiresAt: string | null;
  lastLoginAt: string | null;
  /** The signed-in person's own row. Always false for staff reading the list. */
  isSelf: boolean;
  /** Revoked or removed: history, not somebody still involved. */
  isPast: boolean;
}

/**
 * The invitation a Resend or Revoke acts on.
 *
 * Both sections offer those actions only on a row that has an invitation, so
 * a row without one is a bug; refusing it reports the failure the usual way
 * instead of sending the server a request about no invitation.
 */
export function invitationIdOf(row: Pick<PortalUserRow, 'invitationId'>): string {
  if (!row.invitationId) throw new Error('This row has no invitation.');
  return row.invitationId;
}

/**
 * Turn one row from the backend into what a screen needs.
 *
 * The order of the checks is the point:
 *
 * - removed and revoked are settled facts about the membership, so they win;
 * - active means they accepted and have an account, whatever became of the
 *   invitation afterwards;
 * - otherwise they are still waiting, and the invitation says whether the link
 *   they are holding still works.
 */
export function toPortalUserRow(item: PortalUserListItem): PortalUserRow {
  return {
    membershipId: item.membership_id,
    userId: item.id,
    invitationId: item.invitation_id ?? null,
    name: item.full_name,
    email: item.email,
    role: item.role,
    state: stateOf(item),
    expiresAt: item.invitation_expires_at ?? null,
    lastLoginAt: item.last_login_at ?? null,
    isSelf: item.is_self ?? false,
    isPast: item.is_past ?? false,
  };
}

function stateOf(item: PortalUserListItem): PortalUserState {
  if (item.status === 'REMOVED') return 'removed';
  if (item.status === 'REVOKED') return 'revoked';
  if (item.status === 'ACTIVE') return 'active';
  // INVITED: the membership is waiting. Which of the two waiting states it is
  // depends on the link, not on the membership.
  if (item.invitation_status === 'EXPIRED') return 'expired';
  return 'invited';
}
